import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, ChevronUp, ChevronDown, Save, CircleHelp, Copy, ShieldCheck,
} from 'lucide-react';
import { Button } from './ui/button';

// Shared by the SOW and Design draft pages. Older records store questions as plain
// strings or under other field names, so never assume the shape of one question.
export const normalizeQuestions = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((q) => (typeof q === 'string'
      ? { question: q, why: '' }
      : {
        question: q?.question ?? q?.text ?? '',
        why: q?.why ?? q?.why_we_ask ?? q?.rationale ?? '',
      }))
    .filter((q) => (q.question || '').trim());
};

// ---------------------------------------------------------------------------
// Follow-up questions — consultant-only by house rule.
//
// The generator deliberately keeps these OUT of the document: a customer
// document that asks the customer questions is not a finished document, it is a
// draft. They belong in the covering email or the phone call, so the panel says
// so in as many words and sits above the document rather than inside it.
// ---------------------------------------------------------------------------
export const FollowUpQuestionsPanel = ({ questions, savedAnswers, onSave, onRegenerate, regenerating }) => {
  const [expanded, setExpanded] = useState(true);
  // Keyed by question text — the shape the generator folds in as authoritative answers.
  const [answers, setAnswers] = useState(savedAnswers || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setAnswers(savedAnswers || {}); }, [savedAnswers]);

  if (questions.length === 0) return null;

  const answered = questions.filter(q => (answers[q.question] || '').trim()).length;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(answers);
      toast.success('Answers saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save answers');
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    const text = questions
      .map((q, i) => (q.why ? `${i + 1}. ${q.question}\n   Why: ${q.why}` : `${i + 1}. ${q.question}`))
      .join('\n\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Questions copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy questions');
    }
  };

  return (
    <div
      className="rounded-xl border border-amber-200 bg-amber-50/60 overflow-hidden"
      data-testid="follow-up-panel"
    >
      <div className="flex items-start gap-3 px-4 py-3 md:px-5">
        <CircleHelp className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-amber-900 font-['Plus_Jakarta_Sans']">
            Questions to make this document accurate
            <span className="ml-2 text-[11px] font-medium text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">
              {questions.length}
            </span>
          </h2>
          <p className="text-xs text-amber-800/80 mt-1 leading-relaxed">
            These are <strong className="font-semibold">not in the document</strong> and the customer
            never sees them. Answer what you can (from the customer, the ticket or the system), save, and
            regenerate — the answers go into the next draft as fact.
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            className="h-7 text-xs text-amber-800 hover:text-amber-900 hover:bg-amber-100 px-2"
            data-testid="copy-questions-btn"
          >
            <Copy className="h-3.5 w-3.5" /><span className="hidden md:inline ml-1">Copy</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            className="h-7 text-xs text-amber-800 hover:text-amber-900 hover:bg-amber-100 px-2"
            data-testid="toggle-questions-btn"
          >
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </div>

      {expanded && (
        <ol className="border-t border-amber-200/70 divide-y divide-amber-200/60">
          {questions.map((q, i) => (
            <li key={i} className="px-4 py-3 md:px-5" data-testid={`follow-up-${i}`}>
              <p className="text-[14px] leading-relaxed text-slate-800 font-medium">
                <span className="text-amber-700 mr-1.5">{i + 1}.</span>
                {q.question}
              </p>
              {q.why && (
                <p className="text-xs text-slate-500 mt-1 leading-relaxed pl-5">
                  <span className="font-semibold text-slate-600">Why it matters: </span>
                  {q.why}
                </p>
              )}
              <textarea
                value={answers[q.question] || ''}
                onChange={(e) => setAnswers({ ...answers, [q.question]: e.target.value })}
                placeholder="Answer (optional)"
                rows={2}
                className="mt-2 ml-5 w-[calc(100%-1.25rem)] rounded-md border border-amber-200 bg-white px-2.5 py-1.5 text-sm placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 resize-y"
                data-testid={`follow-up-answer-${i}`}
              />
            </li>
          ))}
          <li className="px-4 py-3 md:px-5 flex flex-wrap items-center gap-2 bg-amber-50/80">
            <span className="text-xs text-amber-800 mr-auto">{answered} of {questions.length} answered</span>
            <Button size="sm" variant="outline" onClick={handleSave} disabled={saving}
              className="h-8 text-xs border-amber-300 text-amber-900" data-testid="save-answers-btn">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              <span className="ml-1">Save answers</span>
            </Button>
            <Button size="sm" onClick={() => onRegenerate(answers)} disabled={regenerating || answered === 0}
              className="h-8 text-xs bg-[#2B8FD8] hover:bg-[#2075b3]" data-testid="regenerate-with-answers-btn">
              <RefreshCw className="h-3.5 w-3.5" /><span className="ml-1">Regenerate with answers</span>
            </Button>
          </li>
        </ol>
      )}
    </div>
  );
};

// The analysis pass's notes: what was read in the sources before drafting —
// commitments, rules, what changes. Internal only; never part of the document.
export const AnalysisNotesPanel = ({ notes }) => {
  const [open, setOpen] = useState(false);
  if (!notes) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="analysis-notes">
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 md:px-5 text-left hover:bg-slate-50">
        <span className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-emerald-600" />
          Analysis notes <span className="font-normal text-xs text-slate-400">— what was checked in the sources before writing (internal)</span>
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>
      {open && (
        <div className="border-t border-slate-100 px-4 py-3 md:px-5 prose prose-sm max-w-none prose-slate">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{notes}</ReactMarkdown>
        </div>
      )}
    </div>
  );
};

export default FollowUpQuestionsPanel;
